import { useCallback, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import useAuth from 'hooks/useAuth';
import { BILLING_CYCLES, CHECKOUT_PRICING, CHECKOUT_STEPS, DEDICATED_PRODUCTS, PRODUCTS, SIGNAL_MANAGER_PRODUCT } from 'lib/billing/config';
import {
  clearStandaloneFlags,
  eaHostingCycleCents as computeEaHostingCycleCents,
  findActiveEaHosting,
  findActiveMcp,
  getUnitAmountCents,
  planHasChanges,
  planTrialSummary,
  trialingProductKeys,
  resolveCatalogPrice,
  resolveEaHostingCheckout
} from 'lib/billing';
import {
  addonEntitlementsFromAccount,
  analyzerOwnerForCycle,
  extractDedicatedArrays,
  pickActiveSubscription,
  pickManageableSubscription,
  resolveFullCheckoutUpdates,
  selectionForCycle,
  summarizeSubscriptions
} from './libBilling';
import { derivePricingBundle, selectActiveProductLines, selectAnalyzerOwnership, type AnalyzerOwnership } from './selectors';
import type { CheckoutCompleteHandler, OverdueInvoice, Selection } from './types';
import { useBillingStore } from './useBillingStore';
import { useCheckoutStore } from './useCheckoutStore';

export interface CheckoutCompatActions {
  setEmail: (v: string) => void;
  setFirstName: (v: string) => void;
  setLastName: (v: string) => void;
  setProduct: (v: string) => void;
  setBillingCycle: (v: string) => void;
  setStandardCount: (v: number) => void;
  setPremiumCount: (v: number) => void;
  setFuturesCount: (v: number) => void;
  setFuturesUnlimited: (v: boolean) => void;
  removeFutures: () => void;
  setSkipTrial: (v: boolean) => void;
  setAnalyzerEnabled: (v: boolean) => void;
  setMcpEnabled: (v: boolean) => void;
  setDedicated: (patch: Partial<Selection>) => void;
  goToPayment: () => void;
  goToConfigure: () => void;
  setSubmitting: (v: boolean) => void;
  setCheckoutSession: (s: unknown | null) => void;
  refreshBilling: () => Promise<void>;
  onComplete: CheckoutCompleteHandler | null;
  [k: string]: any;
}

export interface CheckoutCompatState extends Record<string, any> {
  step?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  submitting?: boolean;
  currency?: string;
  catalog?: any;
  catalogLoading?: boolean;
  catalogError?: unknown;
  checkoutSession?: any;
  authCheckout?: boolean;
  signedInCheckout?: boolean;
  isModifyingExisting?: boolean;
  billingOverview?: any;
  overdueInvoice?: OverdueInvoice | null;
  subscriptions?: any[];
  activeSubscriptions?: any[];
  hasMultipleSubscriptions?: boolean;
  duplicateSubscriptionProducts?: string[];
  activeProductLines?: Set<string>;
  activeSubscription?: any;
  manageableSubscription?: any;
  subscriptionsLoading?: boolean;
  currentAddons?: { analyzer?: boolean; dedicated?: boolean };
  addItemProration?: boolean;
  hasPlanChanges?: boolean;
  analyzerOwnership?: AnalyzerOwnership;
}

export function useCheckoutState(): CheckoutCompatState {
  const selection = useCheckoutStore(useShallow((s) => s.selection));
  const step = useCheckoutStore((s) => s.step);
  const form = useCheckoutStore(useShallow((s) => s.form));
  const submitting = useCheckoutStore((s) => s.submitting);
  const checkoutSession = useCheckoutStore((s) => s.checkoutSession);
  const overdueInvoice = useCheckoutStore((s) => s.overdueInvoice);

  const catalog = useBillingStore((s) => s.catalog);
  const catalogLoading = useBillingStore((s) => s.catalogLoading);
  const catalogError = useBillingStore((s) => s.catalogError);
  const billingOverview = useBillingStore((s) => s.overview);
  const subscriptions = useBillingStore((s) => s.subscriptions);
  const subscriptionsLoading = useBillingStore((s) => s.loading);

  const {
    isAuthenticated,
    isLoading: authLoading,
    accessToken
  } = useAuth() as {
    isAuthenticated?: boolean;
    isLoading?: boolean;
    accessToken?: string;
  };
  const signedInCheckout = Boolean(isAuthenticated && accessToken && !authLoading);
  const authCheckout = signedInCheckout;

  const summary = useMemo(() => summarizeSubscriptions(subscriptions), [subscriptions]);
  const activeSubscription = useMemo(() => pickActiveSubscription(subscriptions), [subscriptions]);
  const manageableSubscription = useMemo(() => pickManageableSubscription(subscriptions), [subscriptions]);

  const billingCycle = (selection as any).billingCycle as string;
  const activeProductLines = useMemo(() => selectActiveProductLines(subscriptions, billingCycle), [subscriptions, billingCycle]);

  const analyzerOwnership = useMemo(
    () => selectAnalyzerOwnership(subscriptions, catalog, billingCycle),
    [subscriptions, catalog, billingCycle]
  );

  const analyzerCycleOwner = useMemo(
    () => analyzerOwnerForCycle(subscriptions, billingCycle, catalog),
    [subscriptions, billingCycle, catalog]
  );

  const currentAddons = useMemo(() => {
    const addOns = (billingOverview as any)?.addOns;
    return addonEntitlementsFromAccount(addOns, subscriptions, catalog);
  }, [billingOverview, subscriptions, catalog]);

  // Products whose one-time trial the user already consumed (per-product gate,
  // authoritative source = BillingOverview). Drives trial-choice visibility,
  // the preview total, and create-vs-add routing so a non-applicable trial is
  // never offered or routed as a standalone sub.
  const trialUsedProductIds = useMemo<string[]>(() => ((billingOverview as any)?.trialUsedProductIds as string[]) || [], [billingOverview]);

  const pricingBundle = useMemo(
    () => derivePricingBundle({ selection, catalog, subscriptions, trialUsedProductIds }),
    [selection, catalog, subscriptions, trialUsedProductIds]
  );

  const isModifyingExisting = Boolean(authCheckout && (activeSubscription as any)?.id);

  // Trial eligibility is per NEW SUBSCRIPTION, not cart-wide. Resolve the plan
  // with skipTrial:false so any trial-eligible product is routed into its own
  // `creates` bucket (detectable even after the buyer picks "Buy now"), then
  // summarise trial state from those buckets. This is what lets a buyer with an
  // existing Standard sub still trial a brand-new Futures product: Standard is
  // an owner-path modify, so it isn't in `creates` and can't veto the trial —
  // unlike the old cart-wide check, which a single non-trial line killed.
  //
  // Standalone EA Hosting summarises the plan the executor actually runs
  // (buildCheckoutPlan), never the copier cart, and has no "Buy now" choice:
  // the drawer opens on Payment, so a skipTrial hydrated from an existing
  // subscription must not suppress the EA trial.
  const eaHostingStandalone = Boolean((selection as any)?.eaHostingStandalone);
  const trialPlanForCapability = useMemo(() => {
    if (!catalog) return null;
    if (eaHostingStandalone) {
      const sel = selection as any;
      return resolveEaHostingCheckout(catalog, sel.billingCycle, sel.eaHostingSlots, subscriptions || []);
    }
    return resolveFullCheckoutUpdates(selection, catalog, subscriptions || [], currentAddons, {
      trialUsedProductIds,
      skipTrial: false
    });
  }, [catalog, selection, subscriptions, currentAddons, trialUsedProductIds, eaHostingStandalone]);
  const skipTrialChoice = !eaHostingStandalone && Boolean((selection as any)?.skipTrial);

  const trialSummary = useMemo(
    () => planTrialSummary(trialPlanForCapability, catalog, trialUsedProductIds, skipTrialChoice),
    [trialPlanForCapability, catalog, trialUsedProductIds, skipTrialChoice]
  );

  // WHICH lines trial, not just whether any does — the summary prices a
  // trialing line at $0 today and says so, and the discount field warns that a
  // code can't reach it. See trialingProductKeys.
  const trialProductKeys = useMemo(
    () => trialingProductKeys(trialPlanForCapability, catalog, trialUsedProductIds, skipTrialChoice),
    [trialPlanForCapability, catalog, trialUsedProductIds, skipTrialChoice]
  );

  const checkoutPlan = useMemo(() => {
    if (!authCheckout || !catalog) return null;
    if (!subscriptions?.length) return null;
    return resolveFullCheckoutUpdates(selection, catalog, subscriptions, currentAddons, {
      trialUsedProductIds,
      skipTrial: Boolean((selection as any)?.skipTrial)
    });
  }, [authCheckout, catalog, subscriptions, selection, currentAddons, trialUsedProductIds]);

  const addItemProration = useMemo(() => {
    if (!checkoutPlan || (checkoutPlan as any).type !== 'multi-cycle') return false;
    return (checkoutPlan as any).ops.some((op: any) => (op.adds?.length || 0) > 0);
  }, [checkoutPlan]);

  const hasPlanChanges = useMemo(() => {
    if (!authCheckout) return true;
    return planHasChanges(checkoutPlan as any); // counts removes (collapse = removes-only)
  }, [authCheckout, checkoutPlan]);

  // Seats dropped by line cancellations — shown in the summary so a $0/credit-only
  // collapse still reads as "N seats removed".
  const removedSeats = useMemo(() => {
    const ops = (checkoutPlan as any)?.ops as any[] | undefined;
    if (!ops?.length) return 0;
    const qtyByItem = new Map<string, number>();
    for (const sub of subscriptions || []) {
      const lines = ((sub as any).lineItems ?? (sub as any).lineItemsList ?? []) as any[];
      for (const li of lines) {
        const id = li.subscriptionItemId ?? li.subscription_item_id;
        if (id) qtyByItem.set(id, Math.max(0, Number(li.quantity) || 0));
      }
    }
    let n = 0;
    for (const op of ops) {
      for (const rm of op.removes || []) n += qtyByItem.get(rm.subscriptionItemId) || 1;
    }
    return n;
  }, [checkoutPlan, subscriptions]);

  const analyzerInfo = useMemo(() => {
    const subOnCycle = (analyzerCycleOwner as any)?.subscription || null;
    const subscriptionItemId =
      analyzerCycleOwner && (analyzerCycleOwner as any).lineCount > 1 ? (analyzerCycleOwner as any).subscriptionItemId : null;
    const subscribed = Boolean(subOnCycle);
    const selected = Boolean((selection as any).analyzerEnabled);
    const hasCopier = pricingBundle.flags.hasCopierAccounts;
    return {
      subscription: subOnCycle,
      subscriptionItemId,
      subscribed,
      on: subscribed || (hasCopier && selected),
      purchasing: hasCopier && selected && !subscribed
    };
  }, [analyzerCycleOwner, selection, pricingBundle.flags.hasCopierAccounts]);

  // AI Connect (MCP) mirrors Analyzer's single-instance ownership: one sub or
  // line entitles the user on any cycle. Only offer line-removal when the sub
  // has other lines; a sole-line sub cancels whole (BE rejects removing the
  // last line).
  const mcpInfo = useMemo(() => {
    const owner = findActiveMcp(subscriptions, catalog as any);
    const subscriptionItemId = owner && owner.lineCount > 1 ? owner.subscriptionItemId : null;
    const subscribed = Boolean(owner);
    const selected = Boolean((selection as any).mcpEnabled);
    return {
      subscription: owner?.subscription || null,
      subscriptionItemId,
      subscribed,
      on: subscribed || selected,
      purchasing: selected && !subscribed
    };
  }, [subscriptions, catalog, selection]);

  // EA Hosting mirrors MCP's single-instance ownership; the slot count lives
  // on the line's quantity and changes ride the standalone flow.
  const eaHostingInfo = useMemo(() => {
    const owner = findActiveEaHosting(subscriptions, catalog as any);
    const subscriptionItemId = owner && owner.lineCount > 1 ? owner.subscriptionItemId : null;
    return {
      subscription: owner?.subscription || null,
      subscriptionItemId,
      subscribed: Boolean(owner)
    };
  }, [subscriptions, catalog]);

  const dedicatedArrays = useMemo(() => extractDedicatedArrays(selection), [selection]);

  // A new Dedicated Environment is always its own Stripe subscription → its own
  // PaymentIntent. The checkout confirms a single PaymentIntent, so a new DE
  // combined with ANY other subscription activity (copier/analyzer for a new
  // buyer, or any non-DE modify/add/create when modifying) would strand one
  // payment and fail. Flag that combo so the UI can require the DE to be bought
  // on its own. (Owned DE rows — subId set — don't count; only brand-new envs.)
  const newDedicatedCount = useMemo(() => {
    const envs = Array.isArray((selection as any).dedicated) ? ((selection as any).dedicated as unknown[]) : [];
    const subIds = Array.isArray((selection as any).dedicatedSubIds) ? ((selection as any).dedicatedSubIds as unknown[]) : [];
    return envs.filter((_v, i) => !subIds[i]).length;
  }, [selection]);

  // New-buyer non-DE activity (copier tier or analyzer). Shared by the two "needs own checkout" gates.
  const stagedTradingActivity = useMemo(
    () => pricingBundle.flags.hasCopierAccounts || analyzerInfo.purchasing,
    [pricingBundle.flags.hasCopierAccounts, analyzerInfo.purchasing]
  );

  const dedicatedNeedsOwnCheckout = useMemo(() => {
    if (newDedicatedCount < 1) return false;
    if (!isModifyingExisting) return stagedTradingActivity;
    // The planner buckets DE creates into the per-cycle op WITHOUT an `isDedi`
    // flag, so classify by price instead: an op carries "other" (non-DE)
    // activity if it has any modify/add (changes to an existing anchor sub →
    // its own PaymentIntent) or a create whose price isn't a DE price. A
    // DE-only add produces a single op whose only create is the DE → no other
    // activity → don't block.
    const dePriceIds = new Set(
      DEDICATED_PRODUCTS.flatMap((prod) => ((catalog as any)?.listForProduct?.(prod) as any[]) || []).map((p: any) => p?.id)
    );
    const ops = ((checkoutPlan as any)?.ops as any[]) || [];
    return ops.some((op: any) => {
      if ((op.modifies?.length || 0) > 0 || (op.adds?.length || 0) > 0) return true;
      return (op.creates || []).some((cr: any) => !dePriceIds.has(cr.price_id));
    });
  }, [newDedicatedCount, isModifyingExisting, checkoutPlan, catalog, stagedTradingActivity]);

  // True only when an EXISTING DE's account count has actually changed — the
  // planner emits a modify keyed by the DE line's subscription_item_id solely on
  // a qty change. Used to hide "add another env" while a DE modify is staged
  // (modify + new create = two payments). Merely owning a DE does NOT block.
  const dedicatedModifyPending = useMemo(() => {
    const ops = ((checkoutPlan as any)?.ops as any[]) || [];
    if (!ops.length) return false;
    const deItemIds = new Set((((selection as any).dedicatedSubItemIds as (string | null)[]) || []).filter(Boolean));
    if (deItemIds.size === 0) return false;
    return ops.some((op: any) => (op.modifies || []).some((m: any) => deItemIds.has(m.subscriptionItemId)));
  }, [checkoutPlan, selection]);

  // The trading pricing bundle doesn't cover a room, and a room is bought alone —
  // so when one is staged, the cart total IS its price.
  const signalManagerCycleCents = useMemo(() => {
    if (!(selection as any).signalManagerSelected || !catalog) return 0;
    const price = resolveCatalogPrice(catalog, SIGNAL_MANAGER_PRODUCT, BILLING_CYCLES.monthly);
    // Quantity-1 package price (checkout sends quantity 1; the graduated first tier is flat).
    return price ? getUnitAmountCents(price) : 0;
  }, [selection, catalog]);

  // Like a room, standalone EA Hosting is bought alone — when staged, the cart
  // total IS its graduated price at the staged slot count.
  const eaHostingCycleCents = useMemo(() => {
    const sel = selection as any;
    if (!sel.eaHostingStandalone || !catalog) return 0;
    return computeEaHostingCycleCents(catalog, sel.billingCycle, sel.eaHostingSlots);
  }, [selection, catalog]);

  return useMemo(
    () => ({
      step,
      email: form.email,
      firstName: form.firstName,
      lastName: form.lastName,
      submitting,
      currency: CHECKOUT_PRICING.currency,
      catalog,
      catalogLoading,
      catalogError,
      checkoutSession,
      authCheckout,
      signedInCheckout,
      isModifyingExisting,
      billingOverview,
      subscriptions,
      activeSubscriptions: summary.activeSubs,
      hasMultipleSubscriptions: summary.hasMultiple,
      duplicateSubscriptionProducts: summary.duplicateProducts,
      activeProductLines,
      activeSubscription,
      manageableSubscription,
      subscriptionsLoading,
      currentAddons,
      trialUsedProductIds,
      addItemProration,
      hasPlanChanges,
      removedSeats,
      dedicatedNeedsOwnCheckout,
      dedicatedModifyPending,
      analyzerOwnership,
      ...selection,
      overdueInvoice,
      ...pricingBundle.pricing,
      // Per-new-sub trial overrides — MUST come after the pricing spread, which
      // sets cart-wide (and here, wrong) trial flags. These are scoped to the
      // plan's `creates` buckets so an existing/owned line can't veto the trial.
      trialCapable: trialSummary.capable,
      trialEligible: trialSummary.eligible,
      trialApplies: trialSummary.applies,
      trialProductKeys,
      // The cart-wide fallbacks describe the copier cart — never EA Hosting's.
      trialDays: trialSummary.days || (eaHostingStandalone ? 0 : (pricingBundle.pricing as any).trialDays) || 0,
      trialIncludedBase: trialSummary.includedBase || (eaHostingStandalone ? 0 : (pricingBundle.pricing as any).trialIncludedBase) || 0,
      analyzerSubscribed: analyzerInfo.subscribed,
      analyzerSubscription: analyzerInfo.subscription,
      analyzerSubscriptionItemId: analyzerInfo.subscriptionItemId,
      analyzerOn: analyzerInfo.on,
      analyzerActive: analyzerInfo.on,
      analyzerPurchasing: analyzerInfo.purchasing,
      analyzerCycleCents: pricingBundle.addonsPricing.analyzerCycleCents,
      mcpSubscribed: mcpInfo.subscribed,
      mcpSubscription: mcpInfo.subscription,
      mcpSubscriptionItemId: mcpInfo.subscriptionItemId,
      mcpOn: mcpInfo.on,
      mcpPurchasing: mcpInfo.purchasing,
      eaHostingSubscribed: eaHostingInfo.subscribed,
      eaHostingSubscription: eaHostingInfo.subscription,
      eaHostingSubscriptionItemId: eaHostingInfo.subscriptionItemId,
      eaHostingCycleCents,
      dedicatedActive: pricingBundle.flags.dedicatedActive,
      dedicatedEnvs: dedicatedArrays.envs,
      dedicatedEnvSubIds: dedicatedArrays.subIds,
      dedicatedEnvSubItemIds: dedicatedArrays.subItemIds,
      dedicatedEnvNicknames: dedicatedArrays.nicknames,
      dedicatedEnvSubLineCounts: dedicatedArrays.subLineCounts,
      dedicatedEnvKinds: dedicatedArrays.kinds,
      dedicatedEnvCount: pricingBundle.flags.dedicatedEnvCount,
      dedicatedAccountsTotal: pricingBundle.flags.dedicatedAccountsTotal,
      dedicatedAllFutures: pricingBundle.flags.dedicatedAllFutures,
      dedicatedCycleCents: pricingBundle.addonsPricing.dedicatedCycleCents,
      addonsTotalCycleCents: pricingBundle.addonsPricing.addonsTotalCycleCents,
      signalManagerCycleCents,
      grandTotalCycleCents: signalManagerCycleCents || eaHostingCycleCents || pricingBundle.totals.grandTotalCycleCents,
      grandTotalWouldBeCycleCents: pricingBundle.totals.grandTotalWouldBeCycleCents,
      grandTotalSavingsCents: pricingBundle.totals.grandTotalSavingsCents,
      grandTotalSavingsPercent: pricingBundle.totals.grandTotalSavingsPercent,
      hasCopierAccounts: pricingBundle.flags.hasCopierAccounts
    }),
    [
      step,
      form,
      submitting,
      catalog,
      catalogLoading,
      catalogError,
      checkoutSession,
      overdueInvoice,
      authCheckout,
      signedInCheckout,
      isModifyingExisting,
      billingOverview,
      subscriptions,
      summary,
      activeProductLines,
      activeSubscription,
      manageableSubscription,
      subscriptionsLoading,
      currentAddons,
      trialUsedProductIds,
      addItemProration,
      hasPlanChanges,
      removedSeats,
      dedicatedNeedsOwnCheckout,
      dedicatedModifyPending,
      signalManagerCycleCents,
      eaHostingCycleCents,
      analyzerOwnership,
      selection,
      pricingBundle,
      analyzerInfo,
      mcpInfo,
      eaHostingInfo,
      dedicatedArrays,
      trialSummary,
      trialProductKeys,
      eaHostingStandalone
    ]
  );
}

export function useCheckoutActions(): CheckoutCompatActions {
  const setSelection = useCheckoutStore((s) => s.setSelection);
  const setForm = useCheckoutStore((s) => s.setForm);
  const goToPayment = useCheckoutStore((s) => s.goToPayment);
  const goToConfigure = useCheckoutStore((s) => s.goToConfigure);
  const setSubmitting = useCheckoutStore((s) => s.setSubmitting);
  const setCheckoutSession = useCheckoutStore((s) => s.setCheckoutSession);
  const onComplete = useCheckoutStore((s) => s.onComplete);

  const fetchBilling = useBillingStore((s) => s.fetch);
  const subscriptions = useBillingStore((s) => s.subscriptions);
  const catalog = useBillingStore((s) => s.catalog);

  const {
    user,
    isAuthenticated,
    isLoading: authLoading,
    accessToken
  } = useAuth() as {
    user?: { tcId?: string };
    isAuthenticated?: boolean;
    isLoading?: boolean;
    accessToken?: string;
  };
  const authCheckout = Boolean(isAuthenticated && accessToken && !authLoading);

  const setEmail = useCallback((v: string) => setForm({ email: v }), [setForm]);
  const setFirstName = useCallback((v: string) => setForm({ firstName: v }), [setForm]);
  const setLastName = useCallback((v: string) => setForm({ lastName: v }), [setForm]);

  const setProduct = useCallback((product: string) => setSelection({ product } as any), [setSelection]);

  const billingCycle = useCheckoutStore((s) => s.selection.billingCycle);
  const setBillingCycle = useCallback(
    (next: string) => {
      if (next === billingCycle) return;
      if (authCheckout) {
        const patch = selectionForCycle(subscriptions, next, catalog);
        setSelection(patch as any);
      } else {
        setSelection({ billingCycle: next } as any);
      }
    },
    [setSelection, authCheckout, subscriptions, catalog, billingCycle]
  );

  const setStandardCount = useCallback((n: number) => setSelection({ standardCount: n } as any), [setSelection]);
  const setPremiumCount = useCallback((n: number) => setSelection({ premiumCount: n } as any), [setSelection]);
  const setFuturesCount = useCallback((n: number) => setSelection({ futuresCount: n } as any), [setSelection]);
  // Switching variant seeds a sensible count for the target plan, mirroring the
  // marketing checkout. Unlimited is a flat whole-product SKU billed as qty=1.
  // Tiered ships an included bundle, so it starts at the included base (the
  // catalog's first tier `up_to`, config fallback otherwise) — never below the
  // floor, which would misrepresent the bundle and under-send the quantity.
  const setFuturesUnlimited = useCallback(
    (v: boolean) => {
      if (v) {
        setSelection({ futuresUnlimited: true, futuresCount: 1 } as any);
        return;
      }
      const price: any = catalog?.get?.(PRODUCTS.futures, billingCycle);
      const upTo = price?.tiers?.[0]?.up_to ?? price?.tiers?.[0]?.upTo;
      const n = Number(upTo);
      const includedBase = Number.isFinite(n) && n > 1 ? n : CHECKOUT_PRICING.futures.tieredIncludedAccounts ?? 20;
      setSelection({ futuresUnlimited: false, futuresCount: includedBase } as any);
    },
    [setSelection, catalog, billingCycle]
  );
  // Fully deselect Futures: clears both the tiered count and the unlimited flag
  // in one patch so the card returns to the "Add" state regardless of variant
  // or trial-lock. (A bare setFuturesCount(0) would leave Unlimited active.)
  const removeFutures = useCallback(() => setSelection({ futuresUnlimited: false, futuresCount: 0 } as any), [setSelection]);

  const setSkipTrial = useCallback((v: boolean) => setSelection({ skipTrial: v } as any), [setSelection]);

  const analyzerOwnership = useMemo(
    () => selectAnalyzerOwnership(subscriptions, catalog, billingCycle),
    [subscriptions, catalog, billingCycle]
  );

  const setAnalyzerEnabled = useCallback(
    (v: boolean) => {
      if (v && analyzerOwnership.ownedElsewhere) return;
      setSelection({ analyzerEnabled: v } as any);
    },
    [setSelection, analyzerOwnership.ownedElsewhere]
  );

  const setMcpEnabled = useCallback((v: boolean) => setSelection({ mcpEnabled: v } as any), [setSelection]);

  const setDedicated = useCallback((patch: any) => setSelection(patch), [setSelection]);

  // Clear every standalone intent on return so a regular checkout can't inherit
  // one. Each routes Payment to a create-only, single-product plan, so a stale
  // flag would silently DROP the other changes staged here (PaymentStep branches
  // on them before the full cart diff). Driven off STANDALONE_SELECTION_FLAGS so
  // a newly added flag is cleared without touching this call site.
  const backToConfigure = useCallback(() => {
    setSelection(clearStandaloneFlags() as any);
    goToConfigure();
  }, [setSelection, goToConfigure]);

  const refreshBilling = useCallback(async () => {
    if (!authCheckout || !user?.tcId) return;
    await fetchBilling(user.tcId, { force: true });
  }, [authCheckout, user?.tcId, fetchBilling]);

  return useMemo(
    () => ({
      setEmail,
      setFirstName,
      setLastName,
      setProduct,
      setBillingCycle,
      setStandardCount,
      setPremiumCount,
      setFuturesCount,
      setFuturesUnlimited,
      removeFutures,
      setSkipTrial,
      setAnalyzerEnabled,
      setMcpEnabled,
      setDedicated,
      goToPayment,
      goToConfigure: backToConfigure,
      setSubmitting,
      setCheckoutSession,
      refreshBilling,
      onComplete
    }),
    [
      setEmail,
      setFirstName,
      setLastName,
      setProduct,
      setBillingCycle,
      setStandardCount,
      setPremiumCount,
      setFuturesCount,
      setFuturesUnlimited,
      removeFutures,
      setSkipTrial,
      setAnalyzerEnabled,
      setMcpEnabled,
      setDedicated,
      goToPayment,
      backToConfigure,
      setSubmitting,
      setCheckoutSession,
      refreshBilling,
      onComplete
    ]
  );
}

// `CHECKOUT_STEPS` re-export so consumers that previously imported it from
// CheckoutRouter or context can keep using the same import path via `./store`.
export { CHECKOUT_STEPS };
